/**
 * DriverFuelWizard - 3-step guided fuel fill wizard with photo capture
 *
 * Step 0: Kilometrage & Type (odometer, fuel_date, fuel_type, is_full_tank, photo odometer)
 * Step 1: Volumes & Montants (liters, total_cost, vendor, card_number, photo pump, photo receipt)
 * Step 2: Recapitulatif (summary + submit)
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Fuel, Camera, CheckCircle, AlertCircle,
  AlertTriangle, Car, Plus
} from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { useFormat } from '../../../i18n/format.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

const FUEL_TYPES = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'gasoline', label: 'Essence' },
  { value: 'electric', label: '\u00C9lectrique' },
  { value: 'hybrid', label: 'Hybride' },
  { value: 'other', label: 'Autre' },
];

const STEPS = [
  { label: 'Kilom\u00E9trage', short: 'KM' },
  { label: 'Volumes', short: '\u20AC' },
  { label: 'R\u00E9capitulatif', short: '\u2713' },
];

function PhotoInput({ label, file, onChange, id }) {
  const inputRef = useRef(null);
  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
      >
        {preview ? (
          <img src={preview} alt={label} className="max-h-32 mx-auto rounded" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-400 dark:text-slate-500">
            <Camera className="h-6 w-6" />
            <span className="text-xs">Prendre une photo</span>
          </div>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={e => onChange(e.target.files?.[0] || null)}
        />
      </div>
    </div>
  );
}

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-1 mb-6">
      {STEPS.map((s, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              i < current ? 'bg-green-500 text-white' :
              i === current ? 'bg-blue-600 text-white' :
              'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
            }`}>
              {i < current ? '\u2713' : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:inline ${
              i === current ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'
            }`}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`flex-1 h-0.5 ${i < current ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function DriverFuelWizard({ onSuccess, onCancel }) {
  const { fmt } = useFormat();
  const { moneyScope, symbolOf } = useOrgCurrency();
  const [step, setStep] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    fuel_date: new Date().toISOString().split('T')[0],
    odometer: '',
    fuel_type: 'diesel',
    is_full_tank: true,
    liters: '',
    total_cost: '',
    vendor: '',
    card_number: '',
    no_receipt: false,
  });

  const [photos, setPhotos] = useState({
    odometer: null,
    pump: null,
    receipt: null,
  });

  useEffect(() => {
    loadPrefill();
  }, []);

  const loadPrefill = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getFuelPrefill();
      if (res.ok && res.data) {
        setPrefill(res.data);
        setForm(prev => ({
          ...prev,
          fuel_type: res.data.vehicle_fuel_type || 'diesel',
        }));
      } else {
        setError(res.error || 'Impossible de charger les informations du v\u00E9hicule');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const computeWarnings = () => {
    const w = [];
    const odo = parseInt(form.odometer);
    if (odo && prefill?.last_odometer && (odo - prefill.last_odometer) > 5000) {
      w.push(`\u00C9cart kilom\u00E9trique important: ${fmt.number(odo - prefill.last_odometer)} km depuis le dernier relev\u00E9`);
    }
    const liters = parseFloat(form.liters);
    const cost = parseFloat(form.total_cost);
    if (liters > 0 && cost > 0) {
      const prixL = cost / liters;
      if (prixL < 0.50 || prixL > 3.00) {
        w.push(`Prix/L inhabituel: ${prixL.toFixed(3)} ${symbolOf()}/L`);
      }
    }
    if (cost > 250) {
      w.push(`Montant \u00E9lev\u00E9: ${moneyScope(cost)}`);
    }
    return w;
  };

  const validateStep0 = () => {
    const odo = parseInt(form.odometer);
    if (odo && prefill?.last_odometer && odo < prefill.last_odometer) {
      setError(`Le kilom\u00E9trage (${odo}) ne peut pas \u00EAtre inf\u00E9rieur au dernier relev\u00E9 (${prefill.last_odometer})`);
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep1 = () => {
    const liters = parseFloat(form.liters);
    const cost = parseFloat(form.total_cost);
    if (!liters || liters <= 0) {
      setError('Le nombre de litres est obligatoire');
      return false;
    }
    if (!cost || cost <= 0) {
      setError('Le montant total est obligatoire');
      return false;
    }
    setError(null);
    return true;
  };

  const goNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    if (step === 1) {
      setWarnings(computeWarnings());
    }
    setStep(prev => Math.min(prev + 1, 2));
  };

  const goBack = () => {
    setError(null);
    setStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const fd = new FormData();
      fd.append('fuel_date', form.fuel_date);
      if (form.odometer) fd.append('odometer', form.odometer);
      fd.append('liters', form.liters);
      fd.append('total_cost', form.total_cost);
      fd.append('fuel_type', form.fuel_type);
      fd.append('is_full_tank', form.is_full_tank ? 'true' : 'false');
      if (form.vendor) fd.append('vendor', form.vendor);
      if (form.card_number) fd.append('card_number', form.card_number);

      if (photos.odometer) fd.append('photo_odometer', photos.odometer);
      if (photos.pump) fd.append('photo_pump', photos.pump);
      if (photos.receipt) fd.append('photo_receipt', photos.receipt);

      const res = await driverMonoAPI.submitFuelFill(fd);
      if (res.ok) {
        setResult(res.data);
      } else {
        setError(res.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setError(err.message || 'Erreur r\u00E9seau');
    } finally {
      setSubmitting(false);
    }
  };

  // --- SUCCESS STATE ---
  if (result) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {"Plein enregistr\u00E9 !"}
        </h3>
        <div className="text-sm text-gray-600 dark:text-slate-300 space-y-1 mb-4">
          <p>{parseFloat(form.liters).toFixed(1)} L &mdash; {moneyScope(parseFloat(form.total_cost))}</p>
          {result.consumption && (
            <p className={`font-medium ${
              result.consumption < 7 ? 'text-green-600 dark:text-green-400' :
              result.consumption <= 10 ? 'text-amber-600 dark:text-amber-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              Consommation: {result.consumption} L/100km
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setResult(null); setStep(0); setForm(prev => ({ ...prev, odometer: '', liters: '', total_cost: '', vendor: '', card_number: '', no_receipt: false })); setPhotos({ odometer: null, pump: null, receipt: null }); setWarnings([]); }}
            className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            Nouveau plein
          </button>
          <button
            onClick={onSuccess}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
          >
            Retour \u00E0 l'historique
          </button>
        </div>
      </div>
    );
  }

  // --- LOADING ---
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // --- PREFILL ERROR ---
  if (!prefill) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-400">{error || 'Impossible de charger les donn\u00E9es'}</p>
        </div>
        <button onClick={onCancel} className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Retour
        </button>
      </div>
    );
  }

  // --- WIZARD ---
  const prixL = (parseFloat(form.liters) > 0 && parseFloat(form.total_cost) > 0)
    ? (parseFloat(form.total_cost) / parseFloat(form.liters)).toFixed(3)
    : null;

  return (
    <div className="space-y-4">
      {/* Back button */}
      <button
        onClick={step === 0 ? onCancel : goBack}
        className="flex items-center gap-1 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        {step === 0 ? 'Annuler' : 'Retour'}
      </button>

      {/* Vehicle banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-3">
        <Car className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-blue-900 dark:text-blue-200">{prefill.vehicle_plate}</span>
          <span className="text-blue-700 dark:text-blue-300 ml-2">{prefill.vehicle_make} {prefill.vehicle_model}</span>
        </div>
      </div>

      <Stepper current={step} />

      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-5">

        {/* === STEP 0: Kilometrage & Type === */}
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{"Kilom\u00E9trage & Type"}</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Date du plein *</label>
              <input
                type="date"
                value={form.fuel_date}
                onChange={e => updateForm('fuel_date', e.target.value)}
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{"Kilom\u00E9trage"}</label>
              <input
                type="number"
                value={form.odometer}
                onChange={e => updateForm('odometer', e.target.value)}
                placeholder={prefill.last_odometer ? `Dernier relev\u00E9: ${fmt.number(prefill.last_odometer)} km` : 'Kilom\u00E9trage au compteur'}
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                min="0"
              />
              {prefill.last_odometer && (
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{"Dernier relev\u00E9 : "}{fmt.number(prefill.last_odometer)} km</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Type de carburant</label>
              <select
                value={form.fuel_type}
                onChange={e => updateForm('fuel_type', e.target.value)}
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {FUEL_TYPES.map(ft => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Type de plein</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => updateForm('is_full_tank', true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.is_full_tank
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400'
                      : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  Plein complet
                </button>
                <button
                  type="button"
                  onClick={() => updateForm('is_full_tank', false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    !form.is_full_tank
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400'
                      : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  Plein partiel
                </button>
              </div>
            </div>

            <PhotoInput
              label="Photo compteur (optionnel)"
              file={photos.odometer}
              onChange={f => setPhotos(prev => ({ ...prev, odometer: f }))}
              id="photo_odometer"
            />
          </div>
        )}

        {/* === STEP 1: Volumes & Montants === */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Volumes & Montants</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Litres *</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={form.liters}
                  onChange={e => updateForm('liters', e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                  min="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm">L</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Montant total *</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  value={form.total_cost}
                  onChange={e => updateForm('total_cost', e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                  min="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-sm">{symbolOf()}</span>
              </div>
            </div>

            {prixL && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 text-center">
                <span className="text-xs text-gray-500 dark:text-slate-400">Prix au litre: </span>
                <span className="font-semibold text-gray-900 dark:text-white">{prixL} {symbolOf()}/L</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Station</label>
              <input
                type="text"
                value={form.vendor}
                onChange={e => updateForm('vendor', e.target.value)}
                placeholder="Nom de la station"
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{"N\u00B0 carte carburant"}</label>
              <input
                type="text"
                value={form.card_number}
                onChange={e => updateForm('card_number', e.target.value)}
                placeholder="Optionnel"
                className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500"
                maxLength={50}
              />
            </div>

            <PhotoInput
              label="Photo pompe (optionnel)"
              file={photos.pump}
              onChange={f => setPhotos(prev => ({ ...prev, pump: f }))}
              id="photo_pump"
            />

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Photo facturette</label>
                <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.no_receipt}
                    onChange={e => { updateForm('no_receipt', e.target.checked); if (e.target.checked) setPhotos(prev => ({ ...prev, receipt: null })); }}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  Pas de facturette
                </label>
              </div>
              {!form.no_receipt && (
                <PhotoInput
                  label=""
                  file={photos.receipt}
                  onChange={f => setPhotos(prev => ({ ...prev, receipt: f }))}
                  id="photo_receipt"
                />
              )}
            </div>
          </div>
        )}

        {/* === STEP 2: Recapitulatif === */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{"R\u00E9capitulatif"}</h3>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 dark:text-slate-400">Date</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {fmt.date(form.fuel_date, { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-400">Type</span>
                <p className="font-medium text-gray-900 dark:text-white">
                  {FUEL_TYPES.find(f => f.value === form.fuel_type)?.label || form.fuel_type}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-400">Litres</span>
                <p className="font-medium text-gray-900 dark:text-white">{parseFloat(form.liters).toFixed(1)} L</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-400">Montant</span>
                <p className="font-medium text-gray-900 dark:text-white">{moneyScope(parseFloat(form.total_cost))}</p>
              </div>
              {prixL && (
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Prix/L</span>
                  <p className="font-medium text-gray-900 dark:text-white">{prixL} {symbolOf()}/L</p>
                </div>
              )}
              {form.odometer && (
                <div>
                  <span className="text-gray-500 dark:text-slate-400">{"Kilom\u00E9trage"}</span>
                  <p className="font-medium text-gray-900 dark:text-white">{fmt.number(parseInt(form.odometer))} km</p>
                </div>
              )}
              {form.vendor && (
                <div>
                  <span className="text-gray-500 dark:text-slate-400">Station</span>
                  <p className="font-medium text-gray-900 dark:text-white">{form.vendor}</p>
                </div>
              )}
              <div>
                <span className="text-gray-500 dark:text-slate-400">Plein</span>
                <p className="font-medium text-gray-900 dark:text-white">{form.is_full_tank ? 'Complet' : 'Partiel'}</p>
              </div>
            </div>

            {/* Consumption preview */}
            {form.is_full_tank && form.odometer && prefill?.last_odometer && parseInt(form.odometer) > prefill.last_odometer && (
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 text-center">
                {(() => {
                  const kmDriven = parseInt(form.odometer) - prefill.last_odometer;
                  const conso = (parseFloat(form.liters) / kmDriven * 100).toFixed(1);
                  const color = conso < 7 ? 'text-green-600 dark:text-green-400' : conso <= 10 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
                  return (
                    <>
                      <span className="text-xs text-gray-500 dark:text-slate-400">{"Consommation estim\u00E9e : "}</span>
                      <span className={`font-bold ${color}`}>{conso} L/100km</span>
                      <span className="text-xs text-gray-400 dark:text-slate-500 ml-1">({fmt.number(kmDriven)} km)</span>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Photo thumbnails */}
            {(photos.odometer || photos.pump || photos.receipt) && (
              <div className="flex gap-2">
                {photos.odometer && <img src={URL.createObjectURL(photos.odometer)} alt="Compteur" className="h-16 w-16 object-cover rounded border border-gray-200 dark:border-slate-600" />}
                {photos.pump && <img src={URL.createObjectURL(photos.pump)} alt="Pompe" className="h-16 w-16 object-cover rounded border border-gray-200 dark:border-slate-600" />}
                {photos.receipt && <img src={URL.createObjectURL(photos.receipt)} alt="Facturette" className="h-16 w-16 object-cover rounded border border-gray-200 dark:border-slate-600" />}
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-1">
                {warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 mt-4">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex gap-3 mt-6">
          {step < 2 ? (
            <button
              onClick={goNext}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Suivant
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-green-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Enregistrer le plein
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
